import { css } from '@linaria/core'
import Modal from '../common/modal'
import chest_coins from '../../assets/coin-shower/chest_coins.webp'
import coin from '../../assets/coin-shower/coin.webp'
import { styled } from '@linaria/react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { HTMLProps, ReactNode, useRef, useState } from 'react'
import { generateRandomInteger } from '../../utils/number'
import twoDigitsFormat from '../../utils/twoDigitsFormat'
import { api } from '../../utils/api-client'
import { ApiResponse } from '../../types/api'
import { MessageModal } from '../common'
import CongratsModal from './congrats-modal'
import closeModal from '../../utils/closeModal'
import LoadingModal from '../common/loading-modal'
import { useTranslation } from 'react-i18next'
import CountDown from './countdown'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import Icon from '../icon'
import coin_explode from '../../assets/coin-shower/coins_explode.gif'

const container = css`
  padding: 0;
  border: none;
  background-color: transparent;
  height: 600px;
  width: 350px;
  @media (min-width: 1200px) {
    width: 500px;
    max-width: 500px;
    height: 750px;
  }
`

const ChestCoins = styled.img`
  position: absolute;
  transition: all 0.8s linear;
  left: 0;
  width: 100%;
  z-index: 1;
`

const CoinExplode = styled.img`
  position: absolute;
  left: 0;
  bottom: 160px;
  width: 100%;
  transition: opacity 0.2s ease-out;
  animation-delay: 0.8s;
  z-index: 2;
  pointer-events: none;
`

const Time = styled.p`
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 0 4px;
  background-color: #2c223a;
  border: 2px solid var(--highlight);
  font-family: 'Syncopate-Bold';
  font-size: 35px;
  width: 180px;
  text-align: center;
  z-index: 2;
  color: #fff;
  border-radius: 10px;
`

const Coin = styled.button`
  @keyframes goDown {
    0% {
      top: 0%;
      opacity: 0;
    }
    5% {
      top: 5%;
      opacity: 1;
    }
    70% {
      top: 70%;
      opacity: 1;
    }
    90% {
      top: 90%;
      opacity: 0;
    }
    100% {
      top: 100%;
      opacity: 0;
    }
  }
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  animation: goDown 2.5s linear forwards;
  color: #fff;
  width: 90px;
  height: 90px;
  padding: 20px;
  img {
    width: 50px;
  }
  p {
    font-family: 'Avenir-Heavy';
    font-size: 18px;
  }
`

const ClickHere = styled.button`
  height: 45px;
  background-color: #df0033;
  border-radius: 2px;
  padding: 0 36px;
  display: block;
  width: max-content;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  p {
    font-size: 16px;
    line-height: 45px;
    color: #fff;
    font-family: 'Syncopate-Bold';
    text-transform: uppercase;
  }
  @media (min-width: 1200px) {
    height: 55px;
    padding: 0 44px;
    p {
      font-size: 18px;
      line-height: 55px;
    }
  }
`

const Close = styled.button`
  display: flex;
  position: absolute;
  padding: 10px;
  top: 220px;
  right: 30px;
  border-radius: 50%;
  border: 2px solid #fff;
  &:hover {
    opacity: 0.8;
  }
  @media (min-width: 1200px) {
    top: 260px;
    right: 0px;
  }
`

const getCoinAmount = (limit?: number) => {
  const percentage = generateRandomInteger(limit ? limit : 1000)
  if (percentage <= 300) return 0
  if (percentage <= 450) return 0.01
  if (percentage <= 550) return 0.04
  if (percentage <= 650) return 0.07
  if (percentage <= 750) return 0.08
  if (percentage <= 850) return 0.09
  if (percentage <= 900) return 1
  if (percentage <= 950) return 1.5
  if (percentage <= 985) return 2
  for (let i = 1; i <= 15; i++) {
    if (percentage <= i + 975) return 3 + (i - 1) * 0.5
  }
  return 0
}

const CoinButton = ({
  amount,
  onClick,
  ...rest
}: Omit<HTMLProps<HTMLButtonElement>, 'type' | 'as'> & { amount: number }) => {
  const [clicked, setClicked] = useState(false)

  return (
    <Coin
      {...rest}
      onClick={e => {
        setClicked(true)
        onClick?.(e)
      }}>
      {clicked ? <p>{amount.toFixed(2)}</p> : <img src={coin} />}
    </Coin>
  )
}

const CoinShowerGame = ({ limit }: { limit: number }) => {
  const { t } = useTranslation(['homeV2'])
  const modal = useModal()
  const { refreshBalance } = useRefreshBalance()
  const messageModal = useModal(MessageModal)
  const congratsModal = useModal(CongratsModal)
  const loadingModal = useModal(LoadingModal)
  const total = useRef<number>(0)
  const [coins, setCoins] = useState<ReactNode[]>([])
  const [timer, setTimer] = useState(15)
  const [start, setStart] = useState(false)

  const claim = async () => {
    try {
      loadingModal.show()
      const res = await api.post<ApiResponse<string>>('/request.php', {
        type: 'claim_bonus',
        bonus: '202311',
        amount: total.current,
      })
      const { status, info } = res.data
      if (status === 1) {
        closeModal(loadingModal)
        closeModal(modal)
        refreshBalance()
        congratsModal.show({ amount: total.current })
      } else {
        closeModal(loadingModal)
        messageModal.show({ message: info })
      }
    } catch (error) {
      console.error(error)
      closeModal(loadingModal)
    }
  }

  const onStart = () => {
    const coinInterval = setInterval(() => {
      const addCoin = generateRandomInteger(2) === 1
      const leftPosition = generateRandomInteger(88)

      if (addCoin) {
        const coinAmount = getCoinAmount(total.current >= limit ? 550 : undefined)
        setCoins(prev => [
          ...prev,
          <CoinButton
            key={prev.length}
            style={{ left: `${leftPosition}%` }}
            amount={coinAmount}
            onClick={() => {
              total.current = total.current + coinAmount
            }}
          />,
        ])
      }
    }, 100)
    const timerInterval = setInterval(() => {
      setTimer(prev => {
        if (prev - 1 > 0) return prev - 1
        clearInterval(timerInterval)
        clearInterval(coinInterval)
        setTimeout(claim, 2500)
        return 0
      })
    }, 1000)
  }

  return (
    <Modal
      id="coin-shower-game"
      containerStyle={container}
      disableOverlayClose={start}>
      {coins}
      {start && timer === 15 && (
        <CountDown startFrom={3} onCountdownEnd={onStart} />
      )}
      {!start && (
        <>
          <Close onClick={() => closeModal(modal)}>
            <Icon id="close" width="15" height="15" fill="#fff" />
          </Close>
          <ClickHere onClick={() => setStart(true)}>
            <p>{t('play-now')}</p>
          </ClickHere>
        </>
      )}
      {start && <Time>00:{twoDigitsFormat(timer)}</Time>}
      <ChestCoins src={chest_coins} style={{ bottom: start ? 0 : 150 }} />
      <CoinExplode
        src={coin_explode}
        style={{
          opacity: !start ? 1 : 0,
        }}
      />
    </Modal>
  )
}

export default NiceModal.create(CoinShowerGame)
